// Cache identity frozen in `@heidi/templates/contracts` (ARCFE-1016).
// Imported as well as re-exported: this module reads it below.
import { GET_TEMPLATES } from '@heidi/templates/contracts';

import { getTemplates } from '@/pages/api/scribeAPIV2';
import { isDesktopOfflineNetworkError } from '@/utils/http/desktopOfflineNetwork';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { toast } from '@heidi/design-system/toast';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

// Moved to `@heidi/templates/data` (S2, ARCFE-1018) — it runs at module scope,
// so it needs the app's queryClient singleton, which now crosses the
// `configureTemplatesData` seam. Re-exported for existing importers.
export { invalidateAllTemplatesQuery } from '@heidi/templates/data';

// paths

type Templates = Awaited<ReturnType<typeof getTemplates>>['data']['templates'];

function getCachedTemplates(): Templates | undefined {
  return reactQueryClient.getQueryData<Templates>([GET_TEMPLATES]);
}

// Deprecated use DocumentTemplates
export function useQueryTemplates() {
  return useQuery({
    queryKey: [GET_TEMPLATES],
    queryFn: async () => {
      try {
        const { data } = await getTemplates();
        return data.templates;
      } catch (error) {
        if (error instanceof AxiosError) {
          if (isDesktopOfflineNetworkError(error)) {
            const cached = getCachedTemplates();
            if (cached) {
              return cached;
            }
            // No cache to fall back on — rethrow so the query enters an error
            // state and refetches on reconnect, rather than caching an empty
            // success that the global staleTime would treat as fresh.
            throw error;
          }

          toast({
            tone: 'destructive',
            title: 'Error',
            description: error.message,
          });
        }
      }
    },
  });
}

export { GET_TEMPLATES };
